import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Wrench, Check, ArrowRight, CheckCircle2, Loader2, XCircle,
  ShieldCheck, Crown, Clock, Calendar, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import LockedNotice from "@/components/LockedNotice";
import PointsRedeemer from "@/components/PointsRedeemer";

const ICON_MAP = { wrench: Wrench, shield: ShieldCheck, crown: Crown };

const ACCENTS = {
  slate: {
    chip: "bg-slate-50 text-slate-700 border-slate-200",
    halo: "from-slate-100 to-slate-50",
    iconBg: "bg-gradient-to-br from-slate-200 to-slate-300 text-slate-700",
    ring: "ring-slate-300",
    bar: "bg-slate-400",
  },
  gold: {
    chip: "bg-[#FFF7E0] text-[#8A6B14] border-[#E8CF7C]",
    halo: "from-[#FFF1C9] to-[#FFFBF0]",
    iconBg: "bg-gradient-to-br from-[#F3E0A2] to-[#E0B847] text-[#3D2C00]",
    ring: "ring-[#C9A356]/40",
    bar: "bg-[#C9A356]",
  },
  navy: {
    chip: "bg-[#E8EEF8] text-[#0A2540] border-[#C7D4E8]",
    halo: "from-slate-200 to-slate-50",
    iconBg: "bg-gradient-to-br from-[#1E3A66] to-[#0A2540] text-white",
    ring: "ring-slate-300",
    bar: "bg-[#0A2540]",
  },
};

function SpecPip({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-md bg-slate-50 px-2.5 py-2 border border-slate-100">
      <Icon className="w-4 h-4 text-[#0A2540] shrink-0" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 leading-none">{label}</div>
        <div className="text-sm font-medium text-[#0A2540] leading-tight truncate">{value}</div>
      </div>
    </div>
  );
}

export function PaymentsPage() {
  const [packages, setPackages] = useState([]);
  const [paying, setPaying] = useState(null);
  const [locked, setLocked] = useState(false);
  const [redemption, setRedemption] = useState({});

  useEffect(() => {
    api.get("/packages/maintenance")
      .then((r) => setPackages(r.data))
      .catch((e) => { if (e?.response?.status === 403) setLocked(true); });
  }, []);

  const buy = async (pkg) => {
    setPaying(pkg.package_id);
    try {
      // Maintenance is annual auto-renewing now (iter15).
      const r = await api.post("/subscriptions/checkout", {
        package_id: pkg.package_id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch {
      toast.error("Failed to start checkout");
      setPaying(null);
    }
  };

  if (locked) {
    return <LockedNotice
      title="Payments unlock after consultation"
      body="Maintenance plans, invoices and online payments are available once your account is approved. Schedule a consultation to get started."
      ctaLabel="Schedule consultation"
      ctaTo="/dashboard/services"
    />;
  }

  return (
    <div data-testid="payments-page">
      <p className="overline">Maintenance & Services</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] mt-2">Maintenance Plans</h1>
      <p className="text-[#475569] mb-8 max-w-2xl">Keep your system at peak efficiency. Plans renew annually and can be paused or upgraded any time.</p>

      <div className="grid md:grid-cols-3 gap-6 mb-12">
        {packages.map((p) => {
          const Icon = ICON_MAP[p.icon] || Wrench;
          const a = ACCENTS[p.accent] || ACCENTS.slate;
          const r = redemption[p.package_id] || { points: 0, discount: 0 };
          const finalAmount = Math.max(p.price - r.discount, 0.5);
          const isPaying = paying === p.package_id;
          return (
            <div
              key={p.package_id}
              data-testid={`maint-${p.package_id}`}
              className={`group relative card-pb overflow-hidden flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl ${p.popular ? `ring-2 ${a.ring}` : ""}`}
            >
              <div className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-b ${a.halo} opacity-70 pointer-events-none`} />
              {p.popular && (
                <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#C9A356] text-white text-[11px] font-semibold tracking-wide shadow-sm">
                  <Sparkles className="w-3 h-3" /> RECOMMENDED
                </div>
              )}

              <div className="relative p-7 pb-5">
                <div className={`w-16 h-16 rounded-2xl ${a.iconBg} grid place-items-center shadow-md mb-4 transition-transform group-hover:scale-105`}>
                  <Icon className="w-8 h-8" strokeWidth={1.5} />
                </div>

                <div className={`inline-block px-2 py-0.5 rounded-md border text-[11px] font-medium uppercase tracking-wider ${a.chip} mb-3`}>
                  {p.visits_per_year || 1}× visits / year
                </div>
                <h3 className="font-heading text-2xl font-medium text-[#0A2540]">{p.name}</h3>
                <p className="text-sm text-[#475569] mt-1 min-h-[40px]">{p.best_for}</p>

                <div className="mt-5 flex items-baseline gap-2">
                  <span className="text-4xl font-heading font-bold text-[#0A2540]">${finalAmount.toFixed(0)}</span>
                  {r.discount > 0 && (
                    <span className="text-sm text-[#475569] line-through" data-testid={`maint-${p.package_id}-original`}>
                      ${p.price.toFixed(0)}
                    </span>
                  )}
                  <span className="text-sm text-[#475569]">/{p.interval}</span>
                </div>
              </div>

              <div className="px-7 grid grid-cols-2 gap-2">
                <SpecPip icon={Calendar} label="Visits/yr" value={`${p.visits_per_year || 1}`} />
                <SpecPip icon={Clock} label="Response" value={`${p.response_hours || 48}h`} />
              </div>

              <div className="px-7 pt-5">
                <div className="text-[11px] uppercase tracking-wider text-[#475569] font-medium mb-2">Includes</div>
                <ul className="space-y-1.5 mb-4 min-h-[100px]">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#334155]">
                      <Check className="w-4 h-4 text-[#C9A356] mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <PointsRedeemer
                  amount={p.price}
                  onChange={(points, discount) => setRedemption((s) => ({ ...s, [p.package_id]: { points, discount } }))}
                  testId={`maint-${p.package_id}-redeem`}
                />
              </div>

              <div className="mt-auto p-7 pt-3">
                <button
                  onClick={() => buy(p)}
                  disabled={isPaying}
                  className="btn-primary w-full"
                  data-testid={`buy-${p.package_id}`}
                >
                  {isPaying ? "..." : <>Subscribe <ArrowRight className="w-4 h-4 ml-2" /></>}
                </button>
                <p className="text-[11px] text-center text-[#94A3B8] mt-2">Auto-renews yearly · Cancel anytime · Stripe</p>
              </div>

              <div className={`h-1 w-full ${a.bar}`} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PaymentStatusPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) {
      navigate("/dashboard");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (cancelled) return;
        setTx(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (r.data.status === "expired" || attempts >= 6) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        console.error("Payment status poll failed:", err);
        if (!cancelled) setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [params, navigate]);

  return (
    <div className="max-w-xl mx-auto" data-testid="payment-status-page">
      <div className="card-pb p-12 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-[#C9A356] mx-auto mb-4 animate-spin" />
            <h2 className="font-heading text-2xl text-[#0A2540]">Confirming your payment...</h2>
            <p className="text-[#475569] mt-2">Hold tight, this only takes a moment.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full grid place-items-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl text-[#0A2540]">Payment successful!</h2>
            <p className="text-[#475569] mt-2">{tx?.description} — ${tx?.amount?.toFixed(2)}</p>
            {tx?.points_used > 0 && (
              <p className="text-sm text-[#C9A356] mt-1" data-testid="points-redeemed">
                {tx.points_used} pts redeemed (–${Number(tx.points_discount).toFixed(2)})
              </p>
            )}
            <button onClick={() => navigate("/dashboard")} className="btn-primary mt-6" data-testid="back-to-dashboard">
              Back to Dashboard
            </button>
          </>
        )}
        {status === "failed" && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full grid place-items-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="font-heading text-2xl text-[#0A2540]">Payment not completed</h2>
            <p className="text-[#475569] mt-2 mb-6">Something went wrong or the payment was cancelled.</p>
            <button onClick={() => navigate("/dashboard/payments")} className="btn-primary" data-testid="try-again">
              Try Again
            </button>
          </>
        )}
      </div>
    </div>
  );
}
